import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@lib/api-hooks/useAuth";
import { FilterTypesEnum } from "@tracify/shared";
import axiosInstance from "@lib/axios";
import dayjs from "dayjs";
import { handleApiError } from "@lib/api-hooks/utils/error-handling";
import { formatRecentActivityApiDateParam } from "./recent-activity-utils";

export type Activity = {
  event: string;
  url: string;
  eventId: string;
  time: Date;
  amount: number;
  currency: string;
  isReturningCustomer?: boolean | null;
  // New v2 fields
  userGroupId?: string;
  eventProperties?: Record<string, any>;
  localAmount?: number;
  nItems?: number;
  screen?: string;
  host?: string;
};

export type ActivityFilter = {
  label: string;
  value: string;
  type: string;
  options: Array<{ label: string; value: string }>;
  availableFilter: FilterTypesEnum[];
};

export type RecentActivityOrdering = "timestamp" | "-timestamp";

export const RECENT_ACTIVITY_DEFAULT_ORDERING: RecentActivityOrdering =
  "-timestamp";

export const EVENT_TYPE_OPTIONS: ActivityFilter["options"] = [
  { label: "Page View", value: "pageview" },
  { label: "Product View", value: "productview" },
  { label: "Add to Cart", value: "addtocart" },
  { label: "Purchase", value: "purchase" },
];

export const ACTIVITY_FILTERS: ActivityFilter[] = [
  {
    label: "Event Type",
    value: "event",
    type: "select",
    options: EVENT_TYPE_OPTIONS,
    availableFilter: [FilterTypesEnum.eq, FilterTypesEnum.in],
  },
  {
    label: "Date Range",
    value: "time",
    type: "datetime",
    options: [],
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
];

type FetchHeartbeatParams = {
  storeId: string;
  limit: number;
  eventTypes?: string[];
  isReturningCustomer?: boolean;
  startDate?: Date;
  endDate?: Date;
  offset?: number;
  ordering?: RecentActivityOrdering;
};

const getFallbackDateRange = () => {
  const endDate = dayjs();
  return {
    startDate: endDate.subtract(24, "hour").toDate(),
    endDate: endDate.toDate(),
  };
};

export const buildHeartbeatEventsUrl = ({
  storeId,
  limit,
  eventTypes,
  isReturningCustomer,
  startDate,
  endDate,
  offset,
  ordering,
}: FetchHeartbeatParams) => {
  const fallbackRange = getFallbackDateRange();
  const queryParams = new URLSearchParams();
  const resolvedStartDate = startDate ?? fallbackRange.startDate;
  const resolvedEndDate = endDate ?? fallbackRange.endDate;
  const apiBaseUrl = process.env.NEXT_PUBLIC_TRACIFY_API_BASE_URL;

  if (!apiBaseUrl) {
    throw new Error("Missing NEXT_PUBLIC_TRACIFY_API_BASE_URL");
  }

  queryParams.append("limit", (limit ?? 100).toString());
  queryParams.append("offset", (offset ?? 0).toString());
  queryParams.append(
    "start_date",
    formatRecentActivityApiDateParam(resolvedStartDate)
  );
  queryParams.append(
    "end_date",
    formatRecentActivityApiDateParam(resolvedEndDate)
  );
  if (isReturningCustomer !== undefined) {
    queryParams.append("is_returning_customer", String(isReturningCustomer));
  }

  if (ordering) {
    queryParams.append("ordering", ordering);
  }

  const cleanedEventTypes = Array.from(
    new Set(
      (eventTypes ?? [])
        .map((eventType) => eventType.trim())
        .filter((eventType) => eventType.length > 0)
    )
  );

  cleanedEventTypes.forEach((eventType) => {
    queryParams.append("event_types", eventType);
  });

  return `${apiBaseUrl}/analytics/events/${storeId}?${queryParams.toString()}`;
};

export const fetchHeartbeat = async ({
  storeId,
  limit,
  eventTypes,
  isReturningCustomer,
  startDate,
  endDate,
  offset,
  ordering,
}: FetchHeartbeatParams) => {
  const url = buildHeartbeatEventsUrl({
    storeId,
    limit,
    eventTypes,
    isReturningCustomer,
    startDate,
    endDate,
    offset,
    ordering,
  });

  try {
    const response = await axiosInstance.get(url);
    const transformedEvents: Activity[] = response.data.items.map(
      (event: any) => ({
        event: event.interaction,
        url: event.url,
        eventId: event.event_id,
        time: new Date(event.timestamp),
        amount: event.amount,
        currency: event.currency_code,
        isReturningCustomer: event.is_returning_customer,
        userGroupId: event.user_group_id,
        eventProperties: event.event_properties,
        localAmount: event.local_amount,
        nItems: event.n_items,
        screen: event.screen,
        host: event.host,
      })
    );

    return {
      events: transformedEvents,
      totalEvents: (response.data.count ?? 0) as number,
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const useHeartbeat = ({
  storeIds,
  eventTypes,
  isReturningCustomer,
  startDate,
  endDate,
  limit,
  offset,
  ordering = RECENT_ACTIVITY_DEFAULT_ORDERING,
  enabled = true,
}: {
  storeIds: string[];
  eventTypes?: string[];
  isReturningCustomer?: boolean;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  offset?: number;
  ordering?: RecentActivityOrdering;
  enabled?: boolean;
}) => {
  const { data: authData } = useAuth();

  return useQuery({
    queryKey: [
      "heartbeat",
      {
        storeIds,
        eventTypes,
        isReturningCustomer,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        limit,
        offset,
        ordering,
      },
    ],
    queryFn: async () => {
      const response = await fetchHeartbeat({
        storeId: storeIds[0],
        limit: limit ?? 100,
        offset: offset ?? 0,
        eventTypes,
        isReturningCustomer,
        startDate,
        endDate,
        ordering,
      });

      return response;
    },
    staleTime: 30000,
    gcTime: 30000,
    placeholderData: (previousData) => previousData,
    enabled: enabled && !!authData?.token && storeIds.length > 0,
  });
};
